import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Loader2, Filter, X, CalendarDays } from 'lucide-react';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useView } from '../../contexts/ViewContext';
import { usePortalMode } from '../../hooks/usePortalMode';
import { poLineDashboardAPI, type POLineDashboardData } from '../../lib/api/po-line-dashboard';
import { coreAPI } from '../../lib/api';
import SearchableSelect from '../ui/SearchableSelect';
import { POLineCurrentMonth } from './po-lines/POLineCurrentMonth';
import { POLineCurrentWeek } from './po-lines/POLineCurrentWeek';
import { POLinePerWeek } from './po-lines/POLinePerWeek';
import { PeriodStepper, relativePeriodLabel } from './po-lines/PeriodStepper';
import { POLineYTD } from './po-lines/POLineYTD';
import ForecastStability from './po-lines/ForecastStability';
import type { POLineDrilldownFilters } from './po-lines/poLineDrilldownModes';
import { fytdLabel } from './po-lines/fytdLabel';

type SubTab = 'current_month' | 'current_week' | 'per_week' | 'ytd' | 'last_12_months' | 'forecast_stability';

const SUB_TABS: Array<{ id: SubTab; label: string; sub: string }> = [
  { id: 'current_month', label: 'Current Month', sub: 'this month' },
  { id: 'current_week', label: 'Current Week', sub: 'this week' },
  { id: 'per_week',     label: 'Per Week',     sub: '12-week trend' },
  { id: 'ytd',          label: 'Year to Date', sub: 'fiscal year → today' },   // W3-T12: filled from the payload
  // CR-7312: the 12 complete months ending last month (as the Supplier Portal's Last 12 Months).
  { id: 'last_12_months', label: 'Last 12 Months', sub: 'to end of last month' },
  // FORECAST-FLUCTUATION: customer-side planning view (hidden on the supplier portal below).
  { id: 'forecast_stability', label: 'Forecast Stability', sub: 'movement since last copy' },
];

interface SupplierOption { id: number; name: string }

/** "12 – 18 May 2026" / "28 Apr – 4 May 2026" for a Mon–Sun ISO window (UTC). */
function formatWeekRange(startIso?: string, endIso?: string): string {
  if (!startIso || !endIso) return '';
  const s = new Date(startIso + 'T00:00:00Z');
  const e = new Date(endIso + 'T00:00:00Z');
  const sameMonth = s.getUTCFullYear() === e.getUTCFullYear() && s.getUTCMonth() === e.getUTCMonth();
  const opts = (withMonth: boolean, withYear: boolean): Intl.DateTimeFormatOptions => ({
    day: 'numeric',
    ...(withMonth ? { month: 'short' } : {}),
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
  return `${s.toLocaleDateString('en-GB', opts(!sameMonth, false))} – ${e.toLocaleDateString('en-GB', opts(true, true))}`;
}

/**
 * PO Line Dashboard — the PO-line-grain sibling of the Scheduled Dashboard for
 * PO-line-grain customers (per-customer delivery grain; the platform
 * ``po_line_schedules`` flag is only the default). Same shell: filter bar,
 * month / week selectors, period sub-tabs (Current Month lands first); data
 * from ``/analytics/po-line-dashboard/``.
 */
export function POLineDashboardTab() {
  const { selectedCompanyId, viewKey } = useView();
  const { isSupplier: portalIsSupplier } = usePortalMode();
  const { formatCurrency } = useFormatters();

  // Supplier-portal users: resolve the CUSTOMER they serve (the endpoint is
  // customer-keyed). undefined = resolving, null = nothing to show.
  const [supplierCustomerId, setSupplierCustomerId] = useState<number | null | undefined>(undefined);
  useEffect(() => {
    if (!portalIsSupplier) { setSupplierCustomerId(null); return; }
    let cancelled = false;
    setSupplierCustomerId(undefined);
    (async () => {
      try {
        const resp = await coreAPI.getAccessibleCompanies({ context: 'customer_filter' });
        if (cancelled) return;
        const raw = resp.data as unknown;
        const results: Array<{ id: number }> =
          Array.isArray(raw) ? (raw as Array<{ id: number }>)
          : Array.isArray((raw as { results?: unknown }).results) ? (raw as { results: Array<{ id: number }> }).results
          : [];
        setSupplierCustomerId(results.length > 0 ? results[0]!.id : null);
      } catch {
        if (!cancelled) setSupplierCustomerId(null);
      }
    })();
    return () => { cancelled = true; };
  }, [portalIsSupplier]);

  const [data, setData] = useState<POLineDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noCustomer, setNoCustomer] = useState(false);
  const [activeTab, setActiveTab] = useState<SubTab>('current_month');
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  // Bumped by a drill-down after a row-level OTIF decision so the tiles re-read.
  const [refreshTick, setRefreshTick] = useState(0);
  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  const [supplierId, setSupplierId] = useState<string>('');
  const [partInput, setPartInput] = useState<string>('');
  const [partCommitted, setPartCommitted] = useState<string>('');
  useEffect(() => {
    const t = setTimeout(() => setPartCommitted(partInput.trim()), 400);
    return () => clearTimeout(t);
  }, [partInput]);

  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await coreAPI.getSupplierSitesForFilter();
        if (cancelled) return;
        const raw = resp.data as unknown;
        const results: Array<{ id: number; name: string }> =
          Array.isArray(raw) ? (raw as Array<{ id: number; name: string }>)
          : Array.isArray((raw as { results?: unknown }).results) ? (raw as { results: Array<{ id: number; name: string }> }).results
          : [];
        setSuppliers(results.map((c) => ({ id: c.id, name: c.name || `Supplier ${c.id}` })).sort((a, b) => a.name.localeCompare(b.name)));
      } catch { /* dropdown stays empty */ }
    })();
    return () => { cancelled = true; };
  }, [viewKey]); // supplier options follow the site switcher

  useEffect(() => {
    if (portalIsSupplier && supplierCustomerId === undefined) { setLoading(true); return; }
    if (portalIsSupplier && supplierCustomerId === null) {
      setLoading(false); setError(null); setNoCustomer(true);
      return;
    }
    setNoCustomer(false);
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params: Record<string, string | number> = {};
    const customerId = portalIsSupplier ? supplierCustomerId : (selectedCompanyId ? Number(selectedCompanyId) : null);
    if (customerId) params['customer_id'] = customerId;
    if (supplierId) params['supplier_id'] = Number(supplierId);
    if (partCommitted) params['part_number'] = partCommitted;
    if (weekOffset !== 0) params['week_offset'] = weekOffset;
    if (monthOffset !== 0) params['month_offset'] = monthOffset;
    poLineDashboardAPI
      .get(params)
      .then((resp) => { if (!cancelled) setData(resp.data); })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCompanyId, portalIsSupplier, supplierCustomerId, supplierId, partCommitted, weekOffset, monthOffset, refreshTick]);

  const activeFilters = useMemo(() => {
    const labels: string[] = [];
    if (supplierId) {
      const name = suppliers.find((s) => String(s.id) === supplierId)?.name ?? `Supplier ${supplierId}`;
      labels.push(`Supplier: ${name}`);
    }
    if (partCommitted) labels.push(`Part: ${partCommitted}`);
    return labels;
  }, [supplierId, partCommitted, suppliers]);

  const clearFilters = () => { setSupplierId(''); setPartInput(''); setPartCommitted(''); };

  const filterBar = (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3" role="group" aria-label="Dashboard filters">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <Filter className="h-3.5 w-3.5" />
          <span>Filters</span>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="pld-supplier" className="text-xs text-slate-500">Supplier</label>
          <div className="w-96">
            <SearchableSelect
              options={suppliers.map((s) => ({ value: String(s.id), label: s.name }))}
              value={supplierId}
              onChange={setSupplierId}
              placeholder="All suppliers"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="pld-part" className="text-xs text-slate-500">Part</label>
          <input
            id="pld-part"
            type="text"
            value={partInput}
            onChange={(e) => setPartInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setPartCommitted(partInput.trim());
              if (e.key === 'Escape') { setPartInput(''); setPartCommitted(''); }
            }}
            placeholder="type part # — applies as you type"
            title="Auto-applies after you stop typing. Esc to clear."
            className="h-8 w-56 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
          />
        </div>
        {activeFilters.length > 0 && (
          <button
            onClick={clearFilters}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 hover:border-red-400"
          >
            <X className="h-3.5 w-3.5" /> Clear filters
          </button>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
        <span>Showing every purchase-order line (delivery judged per PO line and receipt). Cancelled lines are excluded.</span>
        {data?.currency && (<><span className="text-slate-300">•</span><span>Values in {data.currency}</span></>)}
        {activeFilters.length > 0 && (
          <>
            <span className="text-slate-300">•</span>
            <span className="text-slate-600">Filtered by {activeFilters.join(' · ')}</span>
          </>
        )}
      </div>
    </div>
  );

  if (loading && !data) {
    return (
      <div className="space-y-6">
        {filterBar}
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading PO line KPIs…</span>
        </div>
      </div>
    );
  }

  if (noCustomer) {
    return (
      <div className="space-y-6">
        {filterBar}
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-14 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
              <CalendarDays className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">No purchase orders yet</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              This dashboard fills in once your customer raises purchase orders against your company.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        {filterBar}
        <Card><CardContent className="py-8 text-center text-destructive">{error}</CardContent></Card>
      </div>
    );
  }

  if (!data) return null;

  // Customer review 11 Sep (item 31): in a GROUP view the payload rolls up
  // every site of the division, and ``data.customer_id`` is only its PRIMARY
  // site. Handing that to the drill-downs pinned a platform admin's rows to
  // one site (the resolver lets an admin's customer_id override the switcher)
  // — "All (324)" over 3 rows. Leave it unset so the drill resolves the same
  // scope the payload did.
  const resolvedCustomerId = portalIsSupplier
    ? (supplierCustomerId ?? data.customer_id ?? undefined)
    : (selectedCompanyId ?? (data.scope === 'group' ? undefined : data.customer_id ?? undefined));
  const sharedFilters: POLineDrilldownFilters = {
    customer_id: resolvedCustomerId,
    supplier_id: supplierId || undefined,
    part_number: partCommitted || undefined,
  };
  const money = (n: number) => formatCurrency(n, data.currency);

  return (
    <div className="space-y-6">
      {filterBar}

      <div className="border-b border-slate-200" role="tablist" aria-label="Time period">
        <div className="flex gap-1">
          {SUB_TABS.filter((t) => !(portalIsSupplier && t.id === 'forecast_stability')).map((t) => {
            const active = t.id === activeTab;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                aria-current={active ? 'page' : undefined}
                onClick={() => setActiveTab(t.id)}
                className={`px-5 py-2.5 text-sm relative transition-colors ${active ? 'text-slate-900 font-semibold' : 'text-slate-500 font-medium hover:text-slate-800'}`}
              >
                <span>{t.label}</span>
                <span className="ml-2 text-[11px] font-normal text-slate-400">{t.id === 'ytd' ? fytdLabel(data?.ytd?.window_start) : t.sub}</span>
                {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-slate-900" />}
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === 'current_month' && (
        <PeriodStepper
          unit="month"
          label={data.current_month?.month_label ?? ''}
          relative={relativePeriodLabel(monthOffset, 'month')}
          offset={monthOffset}
          loading={loading}
          onOffsetChange={setMonthOffset}
        />
      )}

      {activeTab === 'current_week' && (
        <PeriodStepper
          unit="week"
          label={formatWeekRange(data.current_week.window_start, data.current_week.window_end)}
          relative={relativePeriodLabel(weekOffset, 'week')}
          offset={weekOffset}
          loading={loading}
          onOffsetChange={setWeekOffset}
        />
      )}

      <div>
        {activeTab === 'current_month' && <POLineCurrentMonth data={data} formatCurrency={money} filters={sharedFilters} onRefresh={refresh} />}
        {activeTab === 'current_week'  && <POLineCurrentWeek  data={data} formatCurrency={money} filters={sharedFilters} onRefresh={refresh} />}
        {activeTab === 'per_week'     && <POLinePerWeek     data={data} formatCurrency={money} filters={sharedFilters} onRefresh={refresh} />}
        {activeTab === 'ytd'          && <POLineYTD         data={data} formatCurrency={money} filters={sharedFilters} onRefresh={refresh} />}
        {activeTab === 'last_12_months' && <POLineYTD       data={data} formatCurrency={money} filters={sharedFilters} onRefresh={refresh} period="last_12_months" />}
        {activeTab === 'forecast_stability' && !portalIsSupplier && <ForecastStability filters={sharedFilters} />}
      </div>
    </div>
  );
}
